import type { DashboardVariables, DatasetState } from '@/lib/dashRuntime';
import { type Dashboard, runDashboardDataset } from '@/lib/dashboards-api';
import { useCallback, useEffect, useRef, useState } from 'react';

const ROW_CONTAINER_KEYS = ['result', 'results', 'rows', 'data'];
const MAX_ROW_DEPTH = 4;
const defaultsOf = (
    variables: Dashboard['variables'] = {}
): DashboardVariables =>
    Object.fromEntries(
        Object.entries(variables).map(([name, declaration]) => [
            name,
            declaration.default ?? null
        ])
    );

const ERROR_TEXT = /^(database error|error)\b/i;
const ACCESS_ERROR_TEXT =
    /PowerBIEntityNotFound|\b(401|403)\b|forbidden|unauthori[sz]ed|access denied|not authori[sz]ed|row-level security/i;

function parsedJson(value: unknown): unknown {
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return value;
    try {
        return JSON.parse(trimmed);
    } catch {
        return value;
    }
}

export function rowsOf(data: unknown, depth = 0): unknown {
    const value = parsedJson(data);
    if (Array.isArray(value) || !value || typeof value !== 'object') {
        return value;
    }
    if (depth >= MAX_ROW_DEPTH) return value;
    for (const key of ROW_CONTAINER_KEYS) {
        if (!(key in value)) continue;
        const candidate = rowsOf(
            (value as Record<string, unknown>)[key],
            depth + 1
        );
        if (Array.isArray(candidate)) return candidate;
    }
    return value;
}

export function resultErrorOf(data: unknown): string | null {
    const value = parsedJson(data);
    if (typeof value === 'string') {
        return ERROR_TEXT.test(value.trim()) ? value.trim() : null;
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        const error = (value as Record<string, unknown>).error;
        if (typeof error === 'string' && error) return error;
        if (error && typeof error === 'object') return JSON.stringify(error);
    }
    return null;
}

export const accessProblemOf = (message: string): boolean =>
    ACCESS_ERROR_TEXT.test(message);

const loadingState = (previous?: DatasetState): DatasetState =>
    previous?.status === 'ok'
        ? { ...previous, refreshing: true }
        : { status: 'loading', data: [], message: '' };

async function fetchDatasetState(
    dashboardId: string,
    datasetId: string,
    published: boolean,
    variables: DashboardVariables
): Promise<DatasetState> {
    try {
        const { data } = await runDashboardDataset(
            dashboardId,
            datasetId,
            published,
            variables
        );
        const toolError = resultErrorOf(data);
        if (toolError) {
            return {
                status: 'error',
                data: [],
                message: toolError,
                accessDenied: accessProblemOf(toolError)
            };
        }
        return { status: 'ok', data: rowsOf(data), message: '' };
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        return {
            status: 'error',
            data: [],
            message,
            accessDenied: accessProblemOf(message)
        };
    }
}

export function useDashboardData(
    dashboard: Dashboard | undefined,
    published = false
) {
    const [datasets, setDatasets] = useState<Record<string, DatasetState>>({});
    const [variables, setVariablesState] = useState<DashboardVariables>({});
    const variablesRef = useRef<DashboardVariables>({});
    const generation = useRef(0);
    const tickets = useRef<Record<string, number>>({});
    const pendingNames = useRef(new Set<string>());
    const flushTimer = useRef<number>();

    const load = useCallback(
        async (dashboardId: string, datasetId: string, current: number) => {
            const ticket = (tickets.current[datasetId] ?? 0) + 1;
            tickets.current[datasetId] = ticket;
            setDatasets((state) => ({
                ...state,
                [datasetId]: loadingState(state[datasetId])
            }));
            const next = await fetchDatasetState(
                dashboardId,
                datasetId,
                published,
                variablesRef.current
            );
            if (generation.current !== current) return;
            if (tickets.current[datasetId] !== ticket) return;
            setDatasets((state) => ({ ...state, [datasetId]: next }));
        },
        [published]
    );

    const refreshAll = useCallback(() => {
        if (!dashboard) return;
        const current = ++generation.current;
        dashboard.datasets.forEach((dataset) =>
            load(dashboard.id, dataset.id, current)
        );
    }, [dashboard, load]);

    const refresh = useCallback(
        (datasetId: string) => {
            if (!dashboard) return;
            if (!dashboard.datasets.some((d) => d.id === datasetId)) return;
            load(dashboard.id, datasetId, generation.current);
        },
        [dashboard, load]
    );

    const setVariables = useCallback(
        (changes: DashboardVariables) => {
            if (!dashboard) return;
            const changed = Object.keys(changes).filter(
                (name) =>
                    name in dashboard.variables &&
                    !Object.is(variablesRef.current[name], changes[name])
            );
            if (changed.length === 0) return;
            variablesRef.current = {
                ...variablesRef.current,
                ...Object.fromEntries(changed.map((n) => [n, changes[n]]))
            };
            setVariablesState(variablesRef.current);
            changed.forEach((name) => pendingNames.current.add(name));
            window.clearTimeout(flushTimer.current);
            flushTimer.current = window.setTimeout(() => {
                const names = pendingNames.current;
                pendingNames.current = new Set();
                dashboard.datasets
                    .filter((dataset) =>
                        dataset.variables.some((name) => names.has(name))
                    )
                    .forEach((dataset) =>
                        load(dashboard.id, dataset.id, generation.current)
                    );
            }, 0);
        },
        [dashboard, load]
    );

    useEffect(() => {
        if (!dashboard) return;
        variablesRef.current = defaultsOf(dashboard.variables);
        setVariablesState(variablesRef.current);
        setDatasets(
            Object.fromEntries(
                dashboard.datasets.map((dataset) => [
                    dataset.id,
                    loadingState()
                ])
            )
        );
        refreshAll();
        return () => {
            generation.current++;
        };
    }, [dashboard?.id, dashboard?.updatedAt]);

    const isLoading = Object.values(datasets).some(
        (dataset) => dataset.status === 'loading' || dataset.refreshing === true
    );

    return {
        datasets,
        variables,
        isLoading,
        refresh,
        refreshAll,
        setVariables
    };
}
